import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAppContext } from '@/contexts/AppContext';

export type LegalDocType = 'terms' | 'privacy';

export interface LegalVersion {
  document_type: LegalDocType;
  current_version: string;
  effective_date: string;
}

export function useLegalVersions() {
  const [versions, setVersions] = useState<Record<LegalDocType, string>>({ terms: '1.0.0', privacy: '1.0.0' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVersions = async () => {
      try {
        const { data, error } = await supabase
          .from('legal_documents_versions')
          .select('document_type, current_version');

        if (error) {
          console.error('Error fetching legal versions:', error);
        } else if (data) {
          const map: Record<string, string> = {};
          data.forEach((v: any) => { map[v.document_type] = v.current_version; });
          setVersions(prev => ({ ...prev, ...(map as any) }));
        }
      } catch (err) {
        console.error('Fatal error fetching legal versions:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchVersions();
  }, []);

  return { versions, loading };
}

/** Returns whether the user has accepted the latest versions of terms & privacy. */
export function useHasAcceptedLatestLegal() {
  const { user } = useAppContext();
  const { versions, loading: versionsLoading } = useLegalVersions();
  const [accepted, setAccepted] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  const check = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    if (versionsLoading) return;
    try {
      const { data, error } = await supabase
        .from('legal_acceptances')
        .select('document_type, version')
        .eq('user_id', user.id);

      if (error) {
        console.error('Error checking legal acceptance:', error);
        // If we can't check, assume not accepted to be safe, but stop loading
        setAccepted(false);
        setLoading(false);
        return;
      }

      const acceptedMap: Record<string, string[]> = {};
      (data ?? []).forEach((a: any) => {
        acceptedMap[a.document_type] = acceptedMap[a.document_type] || [];
        acceptedMap[a.document_type].push(a.version);
      });

      const ok = (acceptedMap['terms']?.includes(versions.terms) ?? false)
        && (acceptedMap['privacy']?.includes(versions.privacy) ?? false);
      setAccepted(ok);
    } catch (err) {
      console.error('Unexpected error in legal check:', err);
      setAccepted(false);
    } finally {
      setLoading(false);
    }
  }, [user, versions, versionsLoading]);

  useEffect(() => { check(); }, [check]);

  const acceptCurrent = useCallback(async () => {
    if (!user) return;
    const userAgent = navigator.userAgent;
    await supabase.from('legal_acceptances').insert([
      { user_id: user.id, document_type: 'terms', version: versions.terms, user_agent: userAgent },
      { user_id: user.id, document_type: 'privacy', version: versions.privacy, user_agent: userAgent },
    ]);
    setAccepted(true);
  }, [user, versions]);

  return { accepted, loading: loading || versionsLoading, versions, acceptCurrent, refresh: check };
}
